import { FunctionComponent, ReactNode } from 'react';
import {
    Box,
    Card,
    CardContent,
    Checkbox,
    FormControlLabel,
    Grid2,
    TextField,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { GetTicketsSearchParams } from '../api/ticket.api';
import { B2BUser } from './ticketsContainer';

type DateRange = [Date | null, Date | null];

export type TicketsFilterProps = {
    /** Current search query value */
    query: string;
    /** Callback when search query changes */
    onQueryChange: (value: string) => void;
    /** Current ticket search params */
    ticketSearch: GetTicketsSearchParams;
    /** Whether the insurance filter is checked */
    insuranceChecked: boolean;
    /** Callback when insurance checkbox changes */
    onInsuranceChange: (checked: boolean) => void;
    /** Callback when order date range changes */
    onOrderDateRangeChange: (value: DateRange) => void;
    /** Callback when ticket start date range changes */
    onTicketStartDateRangeChange: (value: DateRange) => void;
    /** Callback to reset all filters */
    onReset: () => void;
    /** Translation function */
    t: (key: string) => string;
    /**
     * Render prop for the order date range picker.
     * Receives value and onChange callback.
     */
    renderOrderDateRange: (props: {
        value: DateRange;
        onChange: (value: DateRange) => void;
        fromText: string;
        toText: string;
    }) => ReactNode;
    /**
     * Render prop for the ticket start date range picker.
     * Receives value and onChange callback.
     */
    renderTicketStartDateRange: (props: {
        value: DateRange;
        onChange: (value: DateRange) => void;
        fromText: string;
        toText: string;
    }) => ReactNode;
    /** List of B2B users for the dropdown filter */
    b2bUsers?: B2BUser[];
    /** Callback when B2B user selection changes */
    onB2BUserChange?: (userId: string | undefined) => void;
};

export const TicketsFilter: FunctionComponent<TicketsFilterProps> = ({
    query,
    onQueryChange,
    ticketSearch,
    insuranceChecked,
    onInsuranceChange,
    onOrderDateRangeChange,
    onTicketStartDateRangeChange,
    onReset,
    t,
    renderOrderDateRange,
    renderTicketStartDateRange,
    b2bUsers,
    onB2BUserChange,
}) => {
    const hasFilters = Object.values(ticketSearch).length > 0;

    return (
        <Card sx={{ width: '100%' }}>
            <CardContent
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    px: 4,
                }}
            >
                <Grid2 spacing={3} container sx={{ flexWrap: 'wrap' }}>
                    <Grid2 size={{ xs: 12, md: 3 }}>
                        <TextField
                            value={query}
                            label={t('ticketsPage.fullTextSearch')}
                            onChange={(e) => onQueryChange(e.target.value)}
                            fullWidth
                        />
                    </Grid2>

                    <Grid2 size={{ xs: 12, md: 4.5 }}>
                        {renderOrderDateRange({
                            value: [
                                ticketSearch.orderDateFrom || null,
                                ticketSearch.orderDateTo || null,
                            ],
                            onChange: onOrderDateRangeChange,
                            fromText: t('ticketsPage.orderDateFrom'),
                            toText: t('ticketsPage.orderDateTo'),
                        })}
                    </Grid2>

                    <Grid2 size={{ xs: 12, md: 4.5 }}>
                        {renderTicketStartDateRange({
                            value: [
                                ticketSearch.ticketStartFrom || null,
                                ticketSearch.ticketStartTo || null,
                            ],
                            onChange: onTicketStartDateRangeChange,
                            fromText: t('ticketsPage.startDateFrom'),
                            toText: t('ticketsPage.startDateTo'),
                        })}
                    </Grid2>

                    {b2bUsers && b2bUsers.length > 0 && (
                        <Grid2 size={{ xs: 12, md: 3 }}>
                            <TextField
                                fullWidth
                                label={t('ticketsPage.b2bPartner')}
                                select
                                value={ticketSearch.userId || '-'}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    onB2BUserChange?.(
                                        value === '-' ? undefined : value
                                    );
                                }}
                                SelectProps={{ native: true }}
                            >
                                <option value='-'>-</option>
                                {b2bUsers.map((user) => (
                                    <option key={user.id} value={user.id}>
                                        {user.name}
                                    </option>
                                ))}
                            </TextField>
                        </Grid2>
                    )}
                </Grid2>

                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        width: '100%',
                        mt: { xs: 2, md: 3 },
                    }}
                >
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={insuranceChecked}
                                onChange={(e) =>
                                    onInsuranceChange(e.target.checked)
                                }
                            />
                        }
                        label={t('ticketsPage.showInsuredTickets')}
                    />

                    {hasFilters && (
                        <LoadingButton
                            onClick={onReset}
                            sx={{
                                color: 'text.secondary',
                                ml: 'auto',
                                textTransform: 'capitalize',
                            }}
                            loading={false}
                        >
                            {t('ticketsPage.reset')}
                        </LoadingButton>
                    )}
                </Box>
            </CardContent>
        </Card>
    );
};
